import { useState } from 'react';
import { MapPin, Navigation, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

interface LocationPickerProps {
  onLocationChange?: (location: string) => void;
}

const LocationPicker = ({ onLocationChange }: LocationPickerProps) => {
  const [location, setLocation] = useState<string>('Set Location');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const getCurrentLocation = () => {
    setIsLoading(true);
    
    if (!navigator.geolocation) {
      toast({
        title: 'Geolocation not supported',
        description: 'Your browser does not support geolocation',
        variant: 'destructive',
      });
      setIsLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          
          // Use reverse geocoding to get address
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
            {
              headers: {
                'Accept-Language': 'en',
              },
            }
          );
          
          if (response.ok) {
            const data = await response.json();
            const address = data.address;
            const locationString = address.suburb 
              ? `${address.suburb}, ${address.city || address.town || address.state}`
              : address.city || address.town || address.state || 'Location found';
            
            setLocation(locationString);
            onLocationChange?.(locationString);
            
            toast({
              title: 'Location updated',
              description: locationString,
            });
          } else {
            throw new Error('Geocoding failed');
          }
        } catch (error) {
          console.error('Geocoding error:', error);
          setLocation('Location found');
          toast({
            title: 'Location found',
            description: 'Could not get address details',
          });
        }
        setIsLoading(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Could not get your location';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }
        
        toast({
          title: 'Location Error',
          description: errorMessage,
          variant: 'destructive',
        });
        setIsLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000,
      }
    );
  };

  const presetLocations = [
    'Johannesburg CBD',
    'Sandton',
    'Pretoria',
    'Soweto',
    'Durban',
    'Cape Town',
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="flex items-center space-x-2 max-w-[200px]">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MapPin className="h-4 w-4 shrink-0" />
          )}
          <span className="text-sm truncate">{location}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="start" 
        className="w-56 bg-popover border border-border shadow-lg z-50"
      >
        <DropdownMenuItem
          onClick={getCurrentLocation}
          className="flex items-center space-x-2 cursor-pointer hover:bg-accent"
          disabled={isLoading}
        >
          <Navigation className="h-4 w-4 text-primary" />
          <span>Use current location</span>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <div className="px-2 py-1.5 text-xs text-muted-foreground">
          Popular areas
        </div>
        
        {presetLocations.map((loc) => (
          <DropdownMenuItem
            key={loc}
            onClick={() => {
              setLocation(loc);
              onLocationChange?.(loc);
            }}
            className="flex items-center space-x-2 cursor-pointer hover:bg-accent"
          >
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span>{loc}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LocationPicker;
